/**
 * SchluesselausgabeDialog — Erfassung der Schlüsselausgabe (Issue #164).
 *
 * Wird vom Admin/Empfang ausgelöst, wenn der Mitarbeiter den Schlüssel abholt.
 * Erfasst: km_beginn (Pflicht). Persistiert via Supabase direkt auf
 * pool_reservierungen (schluesselausgabe_am, schluesselausgabe_durch_id, km_beginn).
 */

import { useState } from 'react';
import { Key, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface SchluesselausgabeDialogProps {
  open: boolean;
  reservierungId: string;
  ausgebendeMitarbeiterId: string;
  onClose: () => void;
  onCompleted: () => void;
}

export default function SchluesselausgabeDialog({
  open,
  reservierungId,
  ausgebendeMitarbeiterId,
  onClose,
  onCompleted,
}: SchluesselausgabeDialogProps) {
  const [kmBeginn, setKmBeginn] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const handleSpeichern = async () => {
    const km = parseInt(kmBeginn, 10);
    if (!kmBeginn || isNaN(km) || km < 0) {
      toast.error('Bitte einen gültigen KM-Stand eingeben');
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('pool_reservierungen')
        .update({
          schluesselausgabe_durch_id: ausgebendeMitarbeiterId,
          schluesselausgabe_am: new Date().toISOString(),
          km_beginn: km,
          updated_at: new Date().toISOString(),
        })
        .eq('id', reservierungId)
        .is('schluesselausgabe_am', null) // Idempotenz-Guard: nicht überschreiben
        .select('id');

      if (error) {
        toast.error(`Schlüsselausgabe konnte nicht gespeichert werden: ${error.message}`);
        return;
      }

      if (!data || data.length === 0) {
        toast.info('Schlüsselausgabe wurde bereits protokolliert');
        onClose();
        return;
      }

      toast.success('Schlüsselausgabe protokolliert');
      setKmBeginn('');
      onCompleted();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Schlüsselausgabe protokollieren"
      data-testid="schluessel-ausgabe-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="relative z-10 bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Key className="size-5 text-amber-600" />
          <h2 className="text-base font-semibold">Schlüsselausgabe protokollieren</h2>
        </div>

        <p className="text-sm text-gray-600">
          Bitte KM-Stand des Fahrzeugs bei Übergabe erfassen.
        </p>

        <div className="space-y-2">
          <Label htmlFor="km-beginn-input">KM-Stand Beginn</Label>
          <Input
            id="km-beginn-input"
            data-testid="schluessel-ausgabe-km-input"
            type="number"
            min={0}
            step={1}
            placeholder="z.B. 12500"
            value={kmBeginn}
            onChange={(e) => setKmBeginn(e.target.value)}
            inputMode="numeric"
            aria-label="km Beginn"
          />
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            size="sm"
            data-testid="schluessel-ausgabe-bestaetigen"
            onClick={handleSpeichern}
            disabled={saving || !kmBeginn}
            className="bg-amber-600 text-white hover:bg-amber-700"
          >
            {saving ? (
              <>
                <Loader2 className="size-3.5 animate-spin mr-1.5" />
                Speichern…
              </>
            ) : (
              'Ausgabe bestätigen'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
